import React, {useState, useEffect} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {availablePlansSelector} from '../../../store/selectors/user.selectors';
import GatewayWizardBody from './GatewayWizardBody';
import {GatewayCreateSchema, GatewayGetSchema, ClusterGatewayCreateSchema} from '../../../api/DBSchema/Gateway';
import Api from '../../../api/SMPFetchHelper';
import {ObjectTypes, ApiErrorCodes} from '../../../api/consts';
import {SessionDataGetSchema} from '../../../api/DBSchema/SessionData';
import SMCModal from '../../../components/core/SMCModal';
import {useSMCErrorHandler} from '../../../utils/errorUtils';
import {useIsMountedRef} from '../../../utils/hooks';
import {generateRandomString} from '../../../utils/utils';

export const GatewayTypeValues = {
    smallOfficeAppliance: 'smallOfficeAppliance',
    smallOfficeHA: 'smallOfficeHA',
}

function GatewayWizard({
    isOpen,
    onRequestClose,
    onSuccess,
    isClone,
    isHaMember,
    originalGateway,
    originalGatewayId
}) {
    const { t } = useTranslation();
    const { getErrorMessage } = useSMCErrorHandler();
    const isMountedRef = useIsMountedRef();

    const [isLoading, setIsLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState(null);
    const [availablePlans, setAvailablePlans] = useState([]);

    const [gatewayType, setGatewayType] = useState(GatewayTypeValues.smallOfficeAppliance);
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [managedBySMP, setManagedBySMP] = useState(true);
    const [staticIP, setStaticIP] = useState('');
    const [planId, setPlanId] = useState('');
    const [registrationKey, setRegistrationKey] = useState('');
    const [ownerId, setOwnerId] = useState('');
    const [member1Name, setMember1Name] = useState('');
    const [member2Name, setMember2Name] = useState('');

    const finishWizard = async () => {
        let createGatewayRequestObj = {};
        if (gatewayType === GatewayTypeValues.smallOfficeAppliance) {
            createGatewayRequestObj = {
                [GatewayCreateSchema.name]: name,
                [GatewayCreateSchema.description]: description,
                [GatewayCreateSchema.managed]: managedBySMP,
                [GatewayCreateSchema.staticIP]: staticIP,
                [GatewayCreateSchema.planId]: planId,
                [GatewayCreateSchema.registrationKey]: registrationKey,
                [GatewayCreateSchema.ownerId]: ownerId,
            }
        }
        else if (gatewayType === GatewayTypeValues.smallOfficeHA) {
            createGatewayRequestObj = {
                [ClusterGatewayCreateSchema.name]: name,
                [ClusterGatewayCreateSchema.description]: description,
                [ClusterGatewayCreateSchema.managed]: managedBySMP,
                [ClusterGatewayCreateSchema.staticIP]: staticIP,
                [ClusterGatewayCreateSchema.planId]: planId,
                [ClusterGatewayCreateSchema.ownerId]: ownerId,
                [ClusterGatewayCreateSchema.member1]: member1Name,
                [ClusterGatewayCreateSchema.member2]: member2Name
            }
        }
        setIsLoading(true);
        try {
            if (isClone && originalGatewayId) {
                await Api.cloneObject(gatewayType === GatewayTypeValues.smallOfficeHA ? ObjectTypes.CLUSTER_GATEWAY : ObjectTypes.GATEWAY, createGatewayRequestObj, originalGatewayId);
                if (isMountedRef.current) {
                    onSuccess(name);
                }
            }
            else {
                await Api.createObject(gatewayType === GatewayTypeValues.smallOfficeHA ? ObjectTypes.CLUSTER_GATEWAY : ObjectTypes.GATEWAY, createGatewayRequestObj);
                if (isMountedRef.current) {
                    onSuccess(name);
                }
            }
        } catch (e) {
            if (isMountedRef.current) {
                const errorMessage = getErrorMessage(e);
                if (errorMessage === ApiErrorCodes.GENERAL_ERROR) {
                    alert(t('SMC_GATEWAYWIZ_CREATE_NEW_GATEWAY_GENERAL_ERROR'));
                } else {
                    alert(errorMessage);
                }
            }
        } finally {
            if (isMountedRef.current) {
                setIsLoading(false);
            }
        }
    }

    // Init wizard data
    useEffect(() => {
        setIsLoading(true);
        (async () => {
            try {
                const sessData = await Api.getSessionData({
                    attributes: [SessionDataGetSchema.availablePlans]
                });
                const plans = sessData.data.availablePlans;
                if (Array.isArray(plans)) {
                    setAvailablePlans(plans);
                    // Set plan id to be first in list, or Initial-Plan, if exists
                    let defaultSelectedPlan = plans[0];
                    if (plans.includes('Initial-Plan')) {
                        defaultSelectedPlan = 'Initial-Plan';
                    }
                    setPlanId(defaultSelectedPlan);
                    setIsLoading(false);
                } else {
                    throw new Error(ApiErrorCodes.GENERAL_ERROR);
                }
            } catch (e) {
                setErrorMessage(getErrorMessage(e));
            }
        })();
    }, [getErrorMessage]);

    useEffect(() => {
        if (isOpen) {
            if (isClone && originalGateway) {
                setName('');
                setDescription(originalGateway[GatewayGetSchema.pages.general.pageId][GatewayGetSchema.pages.general.description]);
                setManagedBySMP(originalGateway[GatewayGetSchema.pages.general.pageId][GatewayGetSchema.pages.general.managedBySmp]);
                setStaticIP(originalGateway[GatewayGetSchema.pages.general.pageId][GatewayGetSchema.pages.general.staticIp]);
                setPlanId(originalGateway[GatewayGetSchema.planId]);
                setRegistrationKey(generateRandomString(8));
                setOwnerId(originalGateway[GatewayGetSchema.ownerId]);

                if (originalGateway[GatewayGetSchema.isClusterGateway]) {
                    setGatewayType(GatewayTypeValues.smallOfficeHA);
                }
                else {
                    setGatewayType(GatewayTypeValues.smallOfficeAppliance);
                }
            }
            else {
                setName('');
                setDescription('');
                setManagedBySMP(true);
                setStaticIP('');
                if (Array.isArray(availablePlans)) {
                    // Set plan id to be first in list, or Initial-Plan, if exists
                    let defaultSelectedPlan = availablePlans[0];
                    if (availablePlans.includes('Initial-Plan')) {
                        defaultSelectedPlan = 'Initial-Plan';
                    }
                    setPlanId(defaultSelectedPlan);
                }
                setRegistrationKey(generateRandomString(8));
                setOwnerId('');    
            }
        }
    }, [isOpen, isClone, originalGateway, availablePlans]);

    const wizardVars = {
        gatewayType, setGatewayType,
        name, setName,
        description, setDescription,
        managedBySMP, setManagedBySMP,
        staticIP, setStaticIP,
        planId, setPlanId,
        registrationKey, setRegistrationKey,
        ownerId, setOwnerId,
        member1Name, setMember1Name,
        member2Name, setMember2Name,
        isHaMember,
        availablePlans
    }

    return (
        <SMCModal
            title={isHaMember ? t('SMC_GATEWAYWIZ_CREATE_NEW_GATEWAY_HA_MEMBER') : t('SMC_GATEWAYWIZ_CREATE_NEW_GATEWAY')}
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            width={610}
            height={600}
            loading={isLoading}
            errorMessage={errorMessage}
        >
            <GatewayWizardBody
                onCancel={onRequestClose}
                onFinish={finishWizard}
                wizardVars={wizardVars}
            />
        </SMCModal>
    );
}

const mapStateToProps = state => ({
    availablePlans: availablePlansSelector(state)
});

const mapDispatchToProps = null;

export default connect(mapStateToProps, mapDispatchToProps)(GatewayWizard);